import { cn } from '@/lib/utils';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  FormFieldGroup,
  Input,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

interface Team {
  id: string;
  name: string;
  totalScore: number;
}

interface TeamPointsAdjustDialogProps {
  eventId: string;
  team: Team;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TeamPointsAdjustDialog({
  eventId,
  team,
  open,
  onOpenChange,
}: TeamPointsAdjustDialogProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [points, setPoints] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const adjustMutation = useMutation({
    mutationFn: async () => {
      return apiClient.post(`/events/${eventId}/teams/${team.id}/adjust-points`, {
        points: parseInt(points, 10),
        reason: reason.trim(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['EventTeams', eventId] });
      toast.success('Points adjusted successfully');
      onOpenChange(false);
      setPoints('');
      setReason('');
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to adjust points');
    },
  });

  const validate = (): boolean => {
    const errs: Record<string, string> = {};

    if (!points.trim()) {
      errs.points = 'Points value is required';
    } else if (isNaN(parseInt(points, 10))) {
      errs.points = 'Points must be a number';
    }

    if (!reason.trim()) {
      errs.reason = 'Reason is required';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    adjustMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Adjust Points for {team.name}</DialogTitle>
          <DialogDescription>
            Current score: {team.totalScore} points. Enter a positive or negative value to adjust.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <FormFieldGroup
            name="points"
            label="Points Adjustment"
            required
            error={errors.points}
            description="Use negative values to deduct points"
          >
            <Input
              id="points"
              type="number"
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              placeholder="e.g., 50 or -25"
              className={cn(errors.points && 'border-destructive')}
            />
          </FormFieldGroup>

          <FormFieldGroup name="reason" label="Reason" required error={errors.reason}>
            <Input
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Bonus for early completion"
              className={cn(errors.reason && 'border-destructive')}
            />
          </FormFieldGroup>

          {points && !isNaN(parseInt(points, 10)) && (
            <div className="p-3 rounded-lg bg-muted/50 text-sm">
              <span className="text-muted-foreground">New score will be: </span>
              <span className="font-semibold">
                {team.totalScore + parseInt(points, 10)} points
              </span>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={adjustMutation.isPending}>
            Adjust Points
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
